import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Copy, Check } from 'lucide-react';

interface TempPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  tempPassword: string;
  title?: string;
  description?: string;
  userName?: string;
}

export function TempPasswordDialog({
  open,
  onClose,
  tempPassword,
  title = 'Temporary Credentials',
  description = 'A temporary password has been generated. Please copy and share it securely with the user. This password will not be displayed again.',
  userName,
}: TempPasswordDialogProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password to clipboard', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {userName ? `Account for ${userName} is ready. ` : ''}
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 my-2">
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
            Temporary Password
          </label>
          <div className="flex items-center gap-2 p-3 bg-surface-2 border border-border rounded-lg">
            <code
              data-testid="temp-password-value"
              className="flex-1 font-mono text-sm font-bold text-text-primary select-all break-all"
            >
              {tempPassword}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="shrink-0 flex items-center gap-1.5"
              aria-label="Copy temporary password"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-success-500" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy</span>
                </>
              )}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
